import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { UserAuth } from "../../context/AuthContext";
import Logo from "../../media/LogoHD.png";
import WitchIcon from "../../media/witch.svg";
import {
  FaSearch,
  FaBars,
  FaTimes,
  FaHome,
  FaChalkboardTeacher,
  FaBook,
  FaCalendarAlt,
  FaWallet,
  FaUserCircle,
  FaShieldAlt,
  FaSignOutAlt,
  FaChevronDown,
} from "react-icons/fa";
import ModeSwitch from "./ModeSwitch";
import VerifyEmailBanner from "./VerifyEmailBanner";
import { formatPieces } from "../../utils/pricing";
import { useLang, LanguageSwitch } from "../../i18n/LanguageContext";

const PURPLE = "#502887";

// Lien du menu principal (bureau) avec soulignement de la page active
const DesktopLink = ({ to, children, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `relative px-3 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
        isActive ? "text-prune" : "text-stone-600 hover:text-prune"
      }`
    }
  >
    {({ isActive }) => (
      <>
        {children}
        <span
          className={`absolute left-3 right-3 -bottom-0.5 h-0.5 rounded-full bg-or transition-transform origin-left ${
            isActive ? "scale-x-100" : "scale-x-0"
          }`}
        />
      </>
    )}
  </NavLink>
);

// Lien du menu mobile, avec icône
const MobileLink = ({ to, icon, children, end }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 rounded-xl text-base font-medium transition-colors ${
        isActive ? "bg-prune text-white" : "text-stone-700 hover:bg-lin"
      }`
    }
  >
    <span className="w-5 flex justify-center">{icon}</span>
    {children}
  </NavLink>
);

const HeaderComp = () => {
  const { user, logOut } = UserAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();

  const [menuOpen, setMenuOpen] = useState(false); // menu mobile
  const [accountOpen, setAccountOpen] = useState(false); // menu du compte (bureau)
  const [search, setSearch] = useState("");
  const accountRef = useRef(null);

  const loggedIn = !!user?.hasProfile;
  const isAdmin = user?.role === 0;
  const balance = Math.floor(Number(user?.balance) || 0);
  const firstName = user?.firstName || user?.displayName?.split(" ")[0] || "";

  // Fermer les menus quand on change de page
  useEffect(() => {
    setMenuOpen(false);
    setAccountOpen(false);
  }, [location.pathname, location.search]);

  // Fermer avec Échap, et le menu compte en cliquant ailleurs
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setAccountOpen(false);
      }
    };
    const onClick = (e) => {
      if (accountRef.current && !accountRef.current.contains(e.target)) setAccountOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  // Empêcher la page de défiler derrière le menu mobile ouvert
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const handleSignOut = async () => {
    try {
      await logOut();
      navigate("/");
    } catch (error) {
      console.error(error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    const q = search.trim();
    navigate(q ? `/cours_a_venir?q=${encodeURIComponent(q)}` : "/cours_a_venir");
    setSearch("");
  };

  const renderAvatar = (size = 40) => (
    <div
      className="rounded-full flex items-center justify-center overflow-hidden shrink-0"
      style={{ background: PURPLE, width: size, height: size }}
    >
      {user?.profilePicture ? (
        <img src={user.profilePicture} alt="" className="w-full h-full object-cover" />
      ) : (
        <img src={WitchIcon} alt="" style={{ width: size * 0.9, height: size * 0.9 }} />
      )}
    </div>
  );

  const renderSearch = (className = "") => (
    <form onSubmit={handleSearch} className={className} role="search">
      <div className="relative">
        <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("Rechercher un cours, un prof…")}
          aria-label={t("Rechercher un cours ou un professeur")}
          className="w-full pl-10 pr-4 py-2 rounded-full text-sm text-stone-700 focus:outline-none focus:ring-2 focus:ring-prune"
          style={{ background: "#e8e2d9", border: "none" }}
        />
      </div>
    </form>
  );

  return (
    <header
      className="sticky top-0 z-50 border-b border-lin-dark"
      style={{ background: "rgba(249,245,240,0.96)", backdropFilter: "blur(8px)" }}
    >
      {/* Conteneur plus large que le contenu : le logo se cale plus près du bord gauche */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 h-[72px] flex items-center gap-3">
        {/* Logo */}
        <Link to="/" className="shrink-0" aria-label={t("Tatie Soso — accueil")}>
          <img src={Logo} alt="Tatie Soso" className="h-11 sm:h-12 w-auto" />
        </Link>

        {/* Liens principaux (grand écran) */}
        <nav className="hidden lg:flex items-center ml-4" aria-label={t("Navigation principale")}>
          <DesktopLink to="/" end>{t("Accueil")}</DesktopLink>
          <DesktopLink to="/cours_a_venir">{t("Cours à venir")}</DesktopLink>
          <DesktopLink to="/professeurs">{t("Professeurs")}</DesktopLink>
          {loggedIn && <DesktopLink to="/mes_cours">{t("Mes cours")}</DesktopLink>}
        </nav>

        <div className="flex-1" />

        {/* Recherche (écran moyen et plus) */}
        {renderSearch("hidden 2xl:block w-72")}
        <Link
          to="/cours_a_venir"
          aria-label={t("Rechercher un cours")}
          title={t("Rechercher un cours")}
          className="hidden md:flex 2xl:hidden w-10 h-10 rounded-full items-center justify-center text-stone-600 hover:text-prune hover:bg-lin transition-colors"
        >
          <FaSearch />
        </Link>

        {/* Zone droite */}
        <div className="flex items-center gap-2 shrink-0">
          <LanguageSwitch className="hidden md:inline-flex" />
          {loggedIn && (
            <div className="hidden xl:block">
              <ModeSwitch compact />
            </div>
          )}

          {loggedIn && (
            <Link
              to="/portefeuille"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold text-prune bg-white border border-lin-dark hover:border-or transition-colors"
              title={t("Mon portefeuille")}
            >
              🪙 {balance}
            </Link>
          )}

          {loggedIn ? (
            /* Menu du compte (grand écran) */
            <div className="relative hidden lg:block" ref={accountRef}>
              <button
                onClick={() => setAccountOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={accountOpen}
                className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full hover:bg-lin transition-colors"
              >
                {renderAvatar(38)}
                <span className="text-sm font-semibold text-stone-700 max-w-[110px] truncate">{firstName}</span>
                <FaChevronDown size={11} className={`text-stone-500 transition-transform ${accountOpen ? "rotate-180" : ""}`} />
              </button>

              {accountOpen && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-64 bg-white rounded-2xl shadow-xl border border-lin-dark py-2 overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-stone-100">
                    <p className="font-bold text-stone-800 truncate">
                      {[user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.displayName}
                    </p>
                    <p className="text-xs text-stone-500 truncate">{user?.email}</p>
                  </div>
                  <div className="px-3 py-3 border-b border-stone-100 xl:hidden">
                    <ModeSwitch compact />
                  </div>
                  {[
                    { to: "/account", icon: <FaUserCircle />, label: t("Mon compte") },
                    { to: "/mes_cours", icon: <FaBook />, label: t("Mes cours") },
                    { to: "/portefeuille", icon: <FaWallet />, label: `${t("Portefeuille")} · ${formatPieces(balance, lang)}` },
                    ...(isAdmin ? [{ to: "/adminpage", icon: <FaShieldAlt />, label: t("Administration") }] : []),
                  ].map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      role="menuitem"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-stone-700 hover:bg-creme hover:text-prune"
                    >
                      <span className="text-prune">{item.icon}</span>
                      {item.label}
                    </Link>
                  ))}
                  <button
                    role="menuitem"
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 border-t border-stone-100 mt-1"
                  >
                    <FaSignOutAlt />
                    {t("Déconnexion")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/signin"
              className="hidden sm:inline-block px-5 py-2 rounded-full text-sm font-semibold text-white bg-prune hover:bg-prune-dark transition-colors"
            >
              {t("Connexion")}
            </Link>
          )}

          {/* Bouton menu (petits écrans) */}
          <button
            aria-label={menuOpen ? t("Fermer le menu") : t("Ouvrir le menu")}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden w-10 h-10 rounded-full flex items-center justify-center text-white transition-colors"
            style={{ background: PURPLE }}
          >
            {menuOpen ? <FaTimes size={16} /> : <FaBars size={16} />}
          </button>
        </div>
      </div>

      <VerifyEmailBanner />

      {/* Menu mobile */}
      {menuOpen && (
        <div
          className="lg:hidden absolute inset-x-0 top-full overflow-y-auto border-t border-lin-dark shadow-lg"
          style={{ background: "#f9f5f0", height: "calc(100dvh - 72px)" }}
        >
          <div className="max-w-md mx-auto px-4 py-5 flex flex-col gap-2">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-stone-500">{t("Langue")}</span>
              <LanguageSwitch />
            </div>
            {renderSearch("mb-3")}

            {loggedIn && (
              <Link
                to="/account"
                className="flex items-center gap-3 p-3 mb-2 rounded-2xl bg-white border border-lin-dark"
              >
                {renderAvatar(44)}
                <div className="min-w-0">
                  <p className="font-bold text-stone-800 truncate">{firstName || t("Mon compte")}</p>
                  <p className="text-xs text-stone-500">🪙 {formatPieces(balance, lang)} · {t("voir mon compte")}</p>
                </div>
              </Link>
            )}

            {loggedIn && (
              <div className="mb-2">
                <ModeSwitch />
              </div>
            )}

            <MobileLink to="/" end icon={<FaHome />}>{t("Accueil")}</MobileLink>
            <MobileLink to="/cours_a_venir" icon={<FaCalendarAlt />}>{t("Cours à venir")}</MobileLink>
            <MobileLink to="/professeurs" icon={<FaChalkboardTeacher />}>{t("Professeurs")}</MobileLink>
            {loggedIn && <MobileLink to="/mes_cours" icon={<FaBook />}>{t("Mes cours")}</MobileLink>}
            {loggedIn && <MobileLink to="/portefeuille" icon={<FaWallet />}>{t("Portefeuille")}</MobileLink>}
            {isAdmin && <MobileLink to="/adminpage" icon={<FaShieldAlt />}>{t("Administration")}</MobileLink>}

            <div className="mt-4 pt-4 border-t border-lin-dark">
              {loggedIn ? (
                <button
                  onClick={handleSignOut}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-red-600 font-semibold border border-red-200 bg-white hover:bg-red-50"
                >
                  <FaSignOutAlt /> {t("Déconnexion")}
                </button>
              ) : (
                <Link
                  to="/signin"
                  className="block text-center py-3 rounded-full text-white font-semibold bg-prune hover:bg-prune-dark"
                >
                  {t("Connexion / Inscription")}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default HeaderComp;
